import React from 'react';
import { Package, AlertTriangle, Check, Keyboard } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const LockerGrid = ({ onLockerClick }) => {
  const { lockers, whiteLabel } = useAuth();
  const primaryColor = whiteLabel ? '#2D4A8F' : '#F26724';

  const getLockerColor = (state, type) => {
    if (type === 'Keypad') {
      return { bg: '#E0E0E0', border: '#9E9E9E', text: '#424242' };
    }
    
    switch (state) {
      case 'Free':
        return { bg: '#ECFDF5', border: '#10B981', text: '#065F46' };
      case 'Occupied':
        return { bg: '#FEF3F2', border: primaryColor, text: '#991B1B' };
      case 'Alert':
        return { bg: '#FEF3C7', border: '#F59E0B', text: '#92400E' };
      default:
        return { bg: '#F3F4F6', border: '#9CA3AF', text: '#374151' };
    }
  };

  const getLockerIcon = (state, type, height) => {
    if (type === 'Keypad') {
      return <Keyboard size={height >= 2 ? 24 : 18} />;
    }
    
    const iconSize = height >= 4 ? 32 : height >= 2 ? 24 : 18;
    
    switch (state) {
      case 'Free':
        return <Check size={iconSize} />;
      case 'Occupied':
        return <Package size={iconSize} />;
      case 'Alert':
        return <AlertTriangle size={iconSize} />;
      default:
        return <Package size={iconSize} />;
    }
  };

  const getSizeColor = (type) => {
    switch (type) {
      case 'Small':
        return '#10B981';
      case 'Medium':
        return '#F59E0B';
      case 'Large':
        return '#8B5CF6';
      default:
        return '#9CA3AF';
    }
  };

  const renderColumn = (columnName) => {
    const columnLockers = lockers.filter(l => l.column === columnName);
    
    return (
      <div 
        key={columnName} 
        className="grid gap-2 auto-rows-fr min-w-[140px] md:min-w-0"
        style={{ 
          gridTemplateRows: 'repeat(16, minmax(0, 1fr))',
          height: 'clamp(600px, 75vh, 900px)'
        }}
      >
        {columnLockers.map(locker => {
          const colors = getLockerColor(locker.state, locker.type);
          const rowSpan = locker.height;
          
          return (
            <button
              key={locker.id}
              onClick={() => onLockerClick && onLockerClick(locker)}
              className="rounded-lg border-2 transition-all hover:scale-[1.02] hover:shadow-lg focus:outline-none focus:ring-2 focus:ring-offset-1 flex items-center justify-center min-h-0 overflow-hidden"
              style={{
                backgroundColor: colors.bg,
                borderColor: colors.border,
                color: colors.text,
                gridRow: `span ${rowSpan}`,
                padding: rowSpan >= 4 ? '12px' : rowSpan >= 2 ? '8px' : '6px'
              }}
            >
              {locker.type === 'Keypad' ? (
                <div className="flex flex-col items-center justify-center space-y-1">
                  {getLockerIcon(locker.state, locker.type, locker.height)}
                  <div className="text-xs md:text-sm font-bold whitespace-nowrap">Keypad</div>
                </div>
              ) : (
                <div className="flex items-center justify-between w-full h-full gap-2">
                  {/* Left side: Icon + ID */}
                  <div className="flex flex-col items-center justify-center flex-shrink-0">
                    {getLockerIcon(locker.state, locker.type, locker.height)}
                    <div 
                      className="font-bold mt-1 whitespace-nowrap"
                      style={{ fontSize: rowSpan >= 4 ? '1rem' : rowSpan >= 2 ? '0.875rem' : '0.75rem' }}
                    >
                      {locker.id}
                    </div>
                  </div>
                  
                  {/* Right side: Badge + State + User */}
                  <div className="flex flex-col items-center justify-center gap-1 flex-1 min-w-0">
                    <div 
                      className="px-2 py-0.5 rounded font-semibold text-white whitespace-nowrap"
                      style={{ 
                        backgroundColor: getSizeColor(locker.type),
                        fontSize: rowSpan >= 4 ? '0.875rem' : '0.75rem'
                      }}
                    >
                      {locker.sizeCode}
                    </div>
                    <div 
                      className="font-medium whitespace-nowrap"
                      style={{ fontSize: rowSpan >= 4 ? '0.75rem' : '0.625rem' }}
                    >
                      {locker.state}
                    </div>
                    {locker.user && rowSpan >= 2 && (
                      <div 
                        className="opacity-75 text-center truncate w-full"
                        style={{ fontSize: '0.625rem' }}
                      >
                        {locker.user}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </button>
          );
        })}
      </div>
    );
  };

  return (
    <div className="w-full">
      {/* Desktop: vertical grid */}
      <div className="hidden lg:grid lg:grid-cols-5 gap-4 items-start">
        {['A', 'B', 'C', 'D', 'E'].map(col => renderColumn(col))}
      </div>
      
      {/* Tablet/Mobile: horizontal scroll */}
      <div className="lg:hidden overflow-x-auto pb-4">
        <div className="flex gap-3 min-w-max">
          {['A', 'B', 'C', 'D', 'E'].map(col => renderColumn(col))}
        </div>
      </div>
    </div>
  );
};

export default LockerGrid;
